import * as React from "react";
import type { CollaborateUserRef, ThreadStatus, CollaborateAgreementVersion } from "../types/collaborateThread";
import { addThreadReply, patchThreadStatus } from "../api";
import { useCollaborateStore } from "../store/useCollaborateStore";
import { useCollaborateUserList } from "../hooks/useCollaborateSelectors";
import { formatTimeago } from "../utils/formatTimeago";
import {
  canCurrentUserApproveThread,
  threadNeedsSignoff,
} from "../utils/signoffAccess";
import CollaborateCommentEditor, {
  type CollaborateCommentEditorHandle,
} from "./editor/CollaborateCommentEditor";
import Avatar from "./primitives/Avatar";
import MentionBody from "./primitives/MentionBody";
import { SignoffRouteMeta } from "./primitives/SignoffRouteMeta";
import StatusPill from "./primitives/StatusPill";
import JumpToAnchorButton from "./JumpToAnchorButton";
import { isAnchorSupported } from "../utils/jumpToThreadAnchor";
import { threadHasDocumentLocation } from "../utils/resolveThreadAnchor";
import { showCollabToast } from "./ui/CollabToast";
import CollabSpinner from "./ui/CollabSpinner";
import CollabPostButton from "./ui/CollabPostButton";
import CollabComposeCard from "./ui/CollabComposeCard";

const COMPOSE_PLACEHOLDER =
  "Type / for commands, @ to mention. Press Shift + Enter for a new line.";

const TAG_NA = "__na__";

export type CommentDetailProps = {
  threadId: string;
  versions?: CollaborateAgreementVersion[];
  defaultComposerVersionId?: string | null;
  currentVersionId?: string | null;
};

function resolveReplyVersionId(
  versions: CollaborateAgreementVersion[],
  threadVersionNumber: number,
  fallbackVersionId: string | null,
): string {
  const byThread = versions.find((version) => version.version === threadVersionNumber);
  if (byThread) {
    return byThread.id;
  }
  const preferred = fallbackVersionId?.trim();
  if (preferred && versions.some((version) => version.id === preferred)) {
    return preferred;
  }
  return versions[0]?.id ?? "";
}

const MessageContent: React.FC<{
  author: CollaborateUserRef;
  createdAt: string;
  body: string;
  versionNumber: number;
  versionTagged?: boolean;
  quote?: string;
  signoffAssignee?: CollaborateUserRef | null;
  onQuoteClick?: () => void;
}> = ({
  author,
  createdAt,
  body,
  versionNumber,
  versionTagged = true,
  quote,
  signoffAssignee,
  onQuoteClick,
}) => {
  const timeLabel = formatTimeago(createdAt);
  const showVersion = versionTagged && Number.isFinite(versionNumber) && versionNumber >= 0;

  return (
    <div className="collab-message__content">
      <div className="collab-message__header">
        <span className="collab-message__author">{author.name}</span>
        <span className="collab-message__trailing">
          <span className="collab-message__time">{timeLabel}</span>
          {showVersion ? (
            <span className="collab-version-pill">v{versionNumber}</span>
          ) : null}
        </span>
      </div>
      {signoffAssignee ? (
        <SignoffRouteMeta from={author.name} to={signoffAssignee.name} />
      ) : null}
      {quote ? (
        onQuoteClick ? (
          <button
            type="button"
            className="collab-quoted collab-quoted--clickable"
            onClick={onQuoteClick}
          >
            <div className="collab-quoted__overline">Quoted text</div>
            <div className="collab-quoted__text">&ldquo;{quote}&rdquo;</div>
          </button>
        ) : (
          <div className="collab-quoted">
            <div className="collab-quoted__overline">Quoted text</div>
            <div className="collab-quoted__text">&ldquo;{quote}&rdquo;</div>
          </div>
        )
      ) : null}
      <div className="collab-message__body">
        <MentionBody text={body} />
      </div>
    </div>
  );
};

const CommentDetail: React.FC<CommentDetailProps> = ({
  threadId,
  versions = [],
  defaultComposerVersionId = null,
  currentVersionId = null,
}) => {
  const thread = useCollaborateStore((s) => s.threadsById[threadId]);
  const currentUserId = useCollaborateStore((s) => s.currentUserId);
  const closeThread = useCollaborateStore((s) => s.closeThread);
  const locateThreadInDocument = useCollaborateStore((s) => s.locateThreadInDocument);
  const editorRef = React.useRef<CollaborateCommentEditorHandle | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [statusSubmitting, setStatusSubmitting] = React.useState<ThreadStatus | null>(null);
  const [selectedVersionId, setSelectedVersionId] = React.useState("");
  const [canReply, setCanReply] = React.useState(false);
  const [boldActive, setBoldActive] = React.useState(false);
  const [italicActive, setItalicActive] = React.useState(false);
  const replyVersionInitializedRef = React.useRef<string | null>(null);
  const userList = useCollaborateUserList();

  const syncToolbar = React.useCallback(() => {
    const editor = editorRef.current?.getEditor() ?? null;
    setBoldActive(Boolean(editor?.isActive("bold")));
    setItalicActive(Boolean(editor?.isActive("italic")));
  }, []);

  React.useEffect(() => {
    const interval = window.setInterval(syncToolbar, 200);
    return () => window.clearInterval(interval);
  }, [syncToolbar]);

  const toggleMark = (mark: "bold" | "italic") => {
    const editor = editorRef.current?.getEditor();
    if (!editor || submitting) {
      return;
    }
    if (mark === "bold") {
      editor.chain().focus().toggleBold().run();
    } else {
      editor.chain().focus().toggleItalic().run();
    }
    syncToolbar();
  };

  React.useEffect(() => {
    replyVersionInitializedRef.current = null;
    setSelectedVersionId("");
  }, [threadId]);

  React.useEffect(() => {
    if (replyVersionInitializedRef.current === threadId) {
      return;
    }
    const currentThread = useCollaborateStore.getState().threadsById[threadId];
    if (!currentThread || versions.length === 0) {
      return;
    }
    setSelectedVersionId(
      resolveReplyVersionId(versions, currentThread.versionNumber, defaultComposerVersionId),
    );
    replyVersionInitializedRef.current = threadId;
  }, [threadId, versions, defaultComposerVersionId]);

  if (!thread) {
    return (
      <div className="collab-center">
        <CollabSpinner />
      </div>
    );
  }

  const showSignoff = canCurrentUserApproveThread(thread, currentUserId);
  const signoffThread = threadNeedsSignoff(thread);
  const hasReplies = thread.replies.length > 0;
  const canLocateQuote =
    isAnchorSupported() && threadHasDocumentLocation(thread);
  const onQuoteLocate = canLocateQuote
    ? () => void locateThreadInDocument(threadId, currentVersionId ?? "")
    : undefined;

  const onStatusChange = async (status: ThreadStatus) => {
    if (status !== "approved" && status !== "rejected") {
      return;
    }
    setStatusSubmitting(status);
    try {
      await patchThreadStatus(threadId, status);
    } catch (err) {
      showCollabToast.error(err instanceof Error ? err.message : "Could not update status.");
    } finally {
      setStatusSubmitting(null);
    }
  };

  const onReply = async () => {
    const body = editorRef.current?.getSerializedBody().trim() ?? "";
    if (!body) {
      return;
    }
    const versionTagged = selectedVersionId !== TAG_NA && Boolean(selectedVersionId.trim());
    const agreementVersionId = versionTagged
      ? selectedVersionId
      : resolveReplyVersionId(versions, thread.versionNumber, defaultComposerVersionId);
    setSubmitting(true);
    try {
      await addThreadReply(threadId, body, agreementVersionId || undefined, versionTagged);
      editorRef.current?.clear();
      setCanReply(false);
    } catch (err) {
      showCollabToast.error(err instanceof Error ? err.message : "Could not post reply.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="collab-detail">
      <div className="collab-detail__header">
        <button type="button" className="collab-detail__back" onClick={closeThread}>
          ← Back
        </button>
        <div className="collab-detail__actions">
          <JumpToAnchorButton thread={thread} currentVersionId={currentVersionId} />
          {signoffThread ? <StatusPill status={thread.status} /> : null}
        </div>
      </div>

      <div className="collab-detail__body thin-scroll">
        <div className={`collab-message${hasReplies ? " collab-message--root" : ""}`}>
          <Avatar name={thread.createdBy.name} size={24} />
          <MessageContent
            author={thread.createdBy}
            createdAt={thread.createdAt}
            body={thread.body}
            versionNumber={thread.versionNumber}
            versionTagged={thread.versionTagged !== false}
            quote={thread.quote || undefined}
            signoffAssignee={signoffThread ? thread.assignee : null}
            onQuoteClick={onQuoteLocate}
          />
        </div>

        {hasReplies ? (
          <div className="collab-replies" role="list">
            {thread.replies.map((reply) => (
              <div key={reply.id} role="listitem" className="collab-message collab-message--reply">
                <Avatar name={reply.createdBy.name} size={22} />
                <MessageContent
                  author={reply.createdBy}
                  createdAt={reply.createdAt}
                  body={reply.body}
                  versionNumber={reply.versionNumber}
                  versionTagged={reply.versionTagged !== false}
                />
              </div>
            ))}
          </div>
        ) : null}

        {showSignoff ? (
          <div className="collab-signoff-panel">
            <div className="collab-signoff-panel__title">Awaiting your sign-off</div>
            <div className="collab-signoff-panel__actions">
              <button
                type="button"
                className="collab-btn-approve"
                disabled={statusSubmitting !== null}
                onClick={() => void onStatusChange("approved")}
              >
                {statusSubmitting === "approved" ? "Approving…" : "Approve"}
              </button>
              <button
                type="button"
                className="collab-btn-reject"
                disabled={statusSubmitting !== null}
                onClick={() => void onStatusChange("rejected")}
              >
                {statusSubmitting === "rejected" ? "Rejecting…" : "Reject"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="collab-detail__reply">
        <CollabComposeCard>
          <CollaborateCommentEditor
            ref={editorRef}
            users={userList}
            compact
            enableSlashCommands
            hasDocumentSelection={() => false}
            disabled={submitting}
            placeholder={COMPOSE_PLACEHOLDER}
            onChange={({ isEmpty }) => setCanReply(!isEmpty)}
            onSubmit={() => {
              if (!submitting && canReply) void onReply();
            }}
          />
          <div className="collab-compose-toolbar">
            <div className="collab-compose-toolbar__marks" role="toolbar" aria-label="Formatting">
              <button
                type="button"
                className={`collab-micro-btn${boldActive ? " is-active" : ""}`}
                title="Bold"
                aria-label="Bold"
                aria-pressed={boldActive}
                disabled={submitting}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("bold");
                }}
              >
                B
              </button>
              <button
                type="button"
                className={`collab-micro-btn${italicActive ? " is-active" : ""}`}
                title="Italic"
                aria-label="Italic"
                aria-pressed={italicActive}
                disabled={submitting}
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleMark("italic");
                }}
              >
                <em>I</em>
              </button>
            </div>
            <div className="collab-compose-toolbar__end">
              {versions.length > 0 ? (
                <label className="collab-tag-to">
                  <span>Tag to</span>
                  <select
                    value={selectedVersionId}
                    onChange={(e) => setSelectedVersionId(e.target.value)}
                    disabled={submitting}
                    aria-label="Tag reply to agreement version"
                  >
                    <option value={TAG_NA}>NA</option>
                    {[...versions]
                      .sort((a, b) => b.version - a.version)
                      .map((version) => (
                        <option key={version.id} value={version.id}>
                          {version.label ?? `v${version.version}`}
                        </option>
                      ))}
                  </select>
                </label>
              ) : null}
              <CollabPostButton
                onClick={() => void onReply()}
                disabled={submitting || !canReply}
                loading={submitting}
                ready={canReply}
              >
                Reply
              </CollabPostButton>
            </div>
          </div>
        </CollabComposeCard>
      </div>
    </div>
  );
};

export default CommentDetail;
